/**
 * ArticleNavBar.mjs
 *
 * Reusable bottom navigation bar for article detail pages.
 * - Mobile: Fixed full-width bar with icon buttons
 * - Desktop: Centered floating pill with labels
 * - Theme-aware via CSS custom properties
 * - IntersectionObserver tracks active section
 * - Renders only when sections are registered
 */
import React, { useEffect, useRef, useCallback, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import useIsMounted from '../../hooks/useIsMounted.mjs';
import { setActiveSection } from '../../slices/articleNavSlice.mjs';
import { toggleConversationExpanded, selectIsConversationExpanded } from '../../slices/conversationSlice.mjs';

/* ─── SVG ICON MAP ─── */
const ICONS = {
    summary: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="article-nav-btn__icon">
            <line x1="17" y1="10" x2="3" y2="10" />
            <line x1="21" y1="6" x2="3" y2="6" />
            <line x1="21" y1="14" x2="3" y2="14" />
            <line x1="17" y1="18" x2="3" y2="18" />
        </svg>
    ),
    article: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="article-nav-btn__icon">
            <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
            <polyline points="14 2 14 8 20 8" />
            <line x1="16" y1="13" x2="8" y2="13" />
            <line x1="16" y1="17" x2="8" y2="17" />
            <polyline points="10 9 9 9 8 9" />
        </svg>
    ),
    facts: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="article-nav-btn__icon">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="16" x2="12" y2="12" />
            <line x1="12" y1="8" x2="12.01" y2="8" />
        </svg>
    ),
    sources: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="article-nav-btn__icon">
            <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71" />
            <path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71" />
        </svg>
    ),
    chat: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="article-nav-btn__icon">
            <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
        </svg>
    ),
};

const DEFAULT_ICON = (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="article-nav-btn__icon">
        <circle cx="12" cy="12" r="1" />
        <circle cx="19" cy="12" r="1" />
        <circle cx="5" cy="12" r="1" />
    </svg>
);

const ArticleNavBar = () => {
    const dispatch = useDispatch();
    const isMounted = useIsMounted();
    const sections = useSelector((state) => state.articleNav?.sections ?? []);
    const activeSection = useSelector((state) => state.articleNav?.activeSection);
    const user = useSelector((state) => state.auth?.user);
    const isChatExpanded = useSelector(selectIsConversationExpanded);
    const observerRef = useRef(null);
    const [bouncingId, setBouncingId] = useState(null);
    const [isVisible, setIsVisible] = useState(false);
    const prevActiveSectionRef = useRef(activeSection);

    // Show bar after a short delay for entrance animation
    useEffect(() => {
        if (sections.length > 0) {
            const timer = setTimeout(() => setIsVisible(true), 100);
            return () => clearTimeout(timer);
        } else {
            setIsVisible(false);
        }
    }, [sections.length]);

    // Bounce animation on section change
    useEffect(() => {
        if (activeSection && activeSection !== prevActiveSectionRef.current) {
            setBouncingId(activeSection);
            const timer = setTimeout(() => setBouncingId(null), 400);
            prevActiveSectionRef.current = activeSection;
            return () => clearTimeout(timer);
        }
    }, [activeSection]);

    // IntersectionObserver for scroll tracking
    useEffect(() => {
        if (!isMounted || sections.length === 0) return;

        // Clean up previous observer
        if (observerRef.current) {
            observerRef.current.disconnect();
        }

        const sectionElements = sections
            .map((s) => document.getElementById(s.id))
            .filter(Boolean);

        if (sectionElements.length === 0) return;

        const activeSections = new Map();

        observerRef.current = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        activeSections.set(entry.target.id, entry.intersectionRatio);
                    } else {
                        activeSections.delete(entry.target.id);
                    }
                });

                // Find the section with highest visibility
                if (activeSections.size > 0) {
                    let bestId = null;
                    let bestRatio = 0;
                    activeSections.forEach((ratio, id) => {
                        if (ratio > bestRatio) {
                            bestRatio = ratio;
                            bestId = id;
                        }
                    });
                    if (bestId) {
                        dispatch(setActiveSection(bestId));
                    }
                }
            },
            {
                rootMargin: '-10% 0px -30% 0px',
                threshold: [0, 0.25, 0.5, 0.75, 1],
            }
        );

        sectionElements.forEach((el) => observerRef.current.observe(el));

        return () => {
            if (observerRef.current) {
                observerRef.current.disconnect();
            }
        };
    }, [sections, dispatch]);

    const handleNavClick = useCallback(
        (section) => {
            // Try getElementById first, then fall back to class-based lookup
            let el = document.getElementById(section.id);
            if (!el && section.fallbackSelector) {
                el = document.querySelector(section.fallbackSelector);
            }
            if (el) {
                const offset = 80; // account for sticky header
                const top = el.getBoundingClientRect().top + window.scrollY - offset;
                window.scrollTo({ top, behavior: 'smooth' });
                dispatch(setActiveSection(section.id));
            }
        },
        [dispatch]
    );

    // Don't render if no sections registered, SSR, or chat is expanded
    if (sections.length === 0 || !isMounted || isChatExpanded) return null;

    return (
        <nav
            className={`article-nav-bar ${isVisible ? 'visible' : 'hidden'}`}
            aria-label="Article sections"
        >
            <div className="article-nav-bar__inner">
                {sections.map((section) => {
                    const isActive = activeSection === section.id;
                    const isBouncing = bouncingId === section.id;
                    const icon = ICONS[section.icon] || DEFAULT_ICON;
                    const isChat = section.icon === 'chat';
                    const chatClass = isChat
                        ? (user ? 'article-nav-btn--chat' : 'article-nav-btn--chat-guest')
                        : null;
                    const btnClass = [
                        'article-nav-btn',
                        isActive && 'article-nav-btn--active',
                        isBouncing && 'article-nav-btn--bounce',
                        chatClass,
                    ]
                        .filter(Boolean)
                        .join(' ');

                    // Chat button for unauthenticated users → Link to signup
                    if (isChat && !user) {
                        return (
                            <Link
                                key={section.id}
                                to="/signup"
                                className={btnClass}
                                title="Sign up to chat"
                            >
                                {icon}
                                <span className="article-nav-btn__label">{section.label}</span>
                            </Link>
                        );
                    }

                    // Chat button for authenticated users → toggle chat panel
                    if (isChat && user) {
                        return (
                            <button
                                key={section.id}
                                className={btnClass}
                                onClick={() => dispatch(toggleConversationExpanded())}
                                aria-current={isActive ? 'true' : undefined}
                                title={section.label}
                            >
                                {icon}
                                <span className="article-nav-btn__label">{section.label}</span>
                            </button>
                        );
                    }

                    return (
                        <button
                            key={section.id}
                            className={btnClass}
                            onClick={() => handleNavClick(section)}
                            aria-current={isActive ? 'true' : undefined}
                            title={section.label}
                        >
                            {icon}
                            <span className="article-nav-btn__label">{section.label}</span>
                        </button>
                    );
                })}
            </div>
        </nav>
    );
};

export default ArticleNavBar;
